import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import React from 'react';

import { useMediaQuery } from '@/hooks/shared/useMediaQuery';

import Area from '@/components/apps/layouts/shared/Area';
import { AniSlideUp } from '@/components/shared/animations/basic';

import theme from '@/styles/theme';

import { useSafetyFeatures } from '../useSafetyPageData';
import SafetyEvidenceFigure from './SafetyEvidenceFigure';

const FEATURE_ICON_SIZES = '(min-width: 1024px) 60px, 40px';

/**
 * ### FeaturesSection
 *
 * The three capabilities the page leads with, followed by the one certified
 * figure that backs them. The figure sits inside this section rather than in
 * one of its own because a single number cannot carry a section, while next
 * to the capabilities it reads as their evidence.
 *
 * @returns The rendered features section element
 * @see {@link useSafetyFeatures} The copy rendered here
 * @see {@link SafetyEvidenceFigure} The certified figure under the cards
 */
export default function FeaturesSection() {
  const { t } = useTranslation('common');
  const features = useSafetyFeatures(t);

  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme`screens.lg`})`,
  });

  return (
    <section data-analytics-section="detection_features">
      <Area>
        <ul className="grid gap-[40px] lg:grid-cols-3 lg:gap-[clamp(40px,5vw,60px)]">
          {features.map((feature, index) => (
            <AniSlideUp
              key={feature.id}
              delay={isDesktop ? index * 0.15 : 0}
              element="li"
            >
              <div>
                <div className="relative h-[40px] w-[40px] lg:h-[clamp(40px,5vw,60px)] lg:w-[clamp(40px,5vw,60px)]">
                  <Image
                    className="object-cover"
                    src={feature.icon}
                    alt=""
                    fill
                    sizes={FEATURE_ICON_SIZES}
                  />
                </div>
                <h3 className="mb-[4px] mt-[16px] text-[22px] font-bold leading-[160%] tracking-[-0.44px] lg:mb-[clamp(4px,1vw,12px)] lg:mt-[clamp(16px,2vw,24px)] lg:text-[clamp(22px,2.333vw,28px)] lg:leading-[140%] lg:tracking-[-0.56px]">
                  {feature.title}
                </h3>
                <p className="text-[16px] font-medium leading-[140%] tracking-[-0.32px] text-apps-brandGray600 lg:text-[clamp(16px,1.667vw,20px)] lg:leading-[160%] lg:tracking-[-0.4px]">
                  {feature.description}
                </p>
              </div>
            </AniSlideUp>
          ))}
        </ul>

        <div className="mt-[48px] lg:mt-[clamp(48px,6.667vw,80px)]">
          <SafetyEvidenceFigure />
        </div>
      </Area>
    </section>
  );
}
